import type { ApplySchemaAttributes, NodeSpecOverride } from "@remirror/core"
import type { NodeView, EditorView } from "prosemirror-view"
import type { Node as ProsemirrorNode } from "prosemirror-model"
import { ImageExtension } from "@remirror/extension-image"
import { NodeSelection } from "prosemirror-state"
import spinner from "../../../../../assets/spinnerloader.svg"
import { AttachedFile } from "../../../../../types/attached-file"

// Configuración responsiva
const IMAGE_CONFIG = {
  mobile: {
    maxWidth: 500,  // Increased from 300
    minWidth: 150,
    defaultWidth: 220  // Slightly increased from 200 for better balance
  },
  desktop: {
    maxWidth: 1200, // Increased from 600
    minWidth: 200,
    defaultWidth: 350  // Slightly increased from 300 for better balance
  },
  breakpoint: 768 // px
}

// Utility para detectar dispositivo
const isMobile = () => window.innerWidth < IMAGE_CONFIG.breakpoint

// Utility para calcular dimensiones inteligentes
const calculateSmartDimensions = (
  naturalWidth: number,
  naturalHeight: number,
  currentWidth?: string | null
): { width: string; height: string } => {
  const config = isMobile() ? IMAGE_CONFIG.mobile : IMAGE_CONFIG.desktop
  const aspectRatio = naturalWidth / naturalHeight

  // Si ya tiene un width válido en px, respetarlo dentro de límites
  if (currentWidth && typeof currentWidth === 'string' && currentWidth.includes('px')) {
    const currentPx = parseInt(currentWidth)
    if (!isNaN(currentPx) && currentPx >= config.minWidth && currentPx <= config.maxWidth) {
      return {
        width: currentWidth,
        height: 'auto'
      }
    }
  }

  // Calcular nuevo width basado en dimensiones naturales y aspect ratio
  let targetWidth = config.defaultWidth

  // Para imágenes muy anchas (panorámicas), usar más espacio
  if (aspectRatio > 2) {
    targetWidth = Math.min(config.maxWidth, naturalWidth)
  }
  // Para imágenes extremadamente altas (muy verticales), hacerlas muy pequeñas
  else if (aspectRatio < 0.3) {
    // Imágenes extremadamente altas: usar un porcentaje muy pequeño del default
    targetWidth = Math.max(config.minWidth, Math.min(config.defaultWidth * 0.5, naturalWidth))
  }
  // Para imágenes muy altas (verticales), hacerlas más pequeñas
  else if (aspectRatio < 0.5) {
    // Imágenes muy altas: usar un porcentaje menor del default
    targetWidth = Math.max(config.minWidth, Math.min(config.defaultWidth * 0.75, naturalWidth))
  }
  // Para imágenes verticales normales (aspect ratio entre 0.5 y 1)
  else if (aspectRatio < 1) {
    // Imágenes verticales: usar un porcentaje del default
    targetWidth = Math.max(config.minWidth, Math.min(config.defaultWidth * 0.9, naturalWidth))
  }
  // Para imágenes cuadradas o rectangulares normales
  else {
    targetWidth = Math.min(config.maxWidth, Math.max(config.minWidth, naturalWidth))
  }

  // Asegurar que no exceda el ancho del contenedor
  const containerWidth = isMobile() ? window.innerWidth - 40 : Math.min(1200, window.innerWidth - 80) // More flexible container width
  targetWidth = Math.min(targetWidth, containerWidth)


  return {
    width: `${targetWidth}px`,
    height: 'auto'
  }
}

export class CustomImageExtension extends ImageExtension {

  constructor(
    private mediaFilesMap?: Record<number, AttachedFile>,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    super({ enableResizing: true });
  }

  override createNodeSpec(extra: ApplySchemaAttributes, override: NodeSpecOverride) {
    const spec = super.createNodeSpec(extra, override)

    return {
      ...spec,
      attrs: {
        ...spec.attrs,
        id: { default: null },
        "data-uploading": { default: null },
        "data-progress": { default: null },
        width: { default: "300px" }, // Mantener un default válido
        height: { default: "auto" },
        media_file_id: { default: null },
      },
      toDOM: (node) => {
        const { src, alt, title, id, width, height } = node.attrs
        const uploading = node.attrs["data-uploading"]
        const progress = node.attrs["data-progress"]

        const wrapperAttrs: Record<string, any> = {
          class: "remirror-image-resizable-wrapper",
          id,
          "data-uploading": uploading ?? undefined,
          "data-progress": progress ?? undefined,
        }

        // Determinar la fuente final de la imagen
        let finalSrc = src; // Default to original src

        // Solo usar signed_url si tenemos un media_file_id válido
        if (node.attrs.media_file_id && this.mediaFilesMap?.[node.attrs.media_file_id]) {
          const signedUrl = this.mediaFilesMap[node.attrs.media_file_id].signed_url;
          if (signedUrl) {
            finalSrc = signedUrl;
          }
        }

        const imgAttrs: Record<string, any> = {
          src: finalSrc,
          alt,
          title,
          // style: `width: ${width || '300px'}; height: ${height}; object-fit: contain; display: block; border-radius: 8px; border: 1px solid #e0e0e0;`,
        }

        return ["div", wrapperAttrs, ["img", imgAttrs]] as any
      },
    }
  }

  override createNodeViews() {
    return {
      image: (node, view, getPos) => new CustomImageNodeView(node, view, getPos!, this.mediaFilesMap, this.isOwner, this.blockFilesDownload),
    }
  }
}

export class CustomImageNodeView implements NodeView {
  dom: HTMLDivElement
  img: HTMLImageElement
  resizeHandle: HTMLDivElement
  private originalRatio = 1
  private selected = false
  private naturalDimensions = { width: 0, height: 0 }

  constructor(
    private node: ProsemirrorNode,
    private view: EditorView,
    private getPos: () => number,
    private mediaFilesMap?: Record<number, AttachedFile>,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    const { attrs } = node

    // Crear wrapper
    this.dom = document.createElement("div")
    this.dom.className = "remirror-image-resizable-wrapper"
    this.dom.style.position = "relative"
    this.dom.style.display = "block" // Cambiar a block para mejor comportamiento
    this.dom.style.maxWidth = "100%"
    this.dom.style.margin = "0.5rem 0"

    // Crear imagen
    this.img = document.createElement("img")

    // Determinar la fuente final de la imagen
    let finalSrc = attrs.src; // Default to original src

    // Solo usar signed_url si tenemos un media_file_id válido
    if (this.node.attrs.media_file_id && this.mediaFilesMap?.[this.node.attrs.media_file_id]) {
      const signedUrl = this.mediaFilesMap[this.node.attrs.media_file_id].signed_url;
      if (signedUrl) {
        finalSrc = signedUrl;
      }
    }

    this.img.src = finalSrc
    this.img.alt = attrs.alt || "Imagen"

    // Estilos base para la imagen
    this.img.style.objectFit = "contain" // Cambiar a contain para evitar recortes
    this.img.style.borderRadius = "8px"
    this.img.style.display = "block"
    this.img.style.border = "1px solid #e0e0e0"
    this.img.style.width = "100%"
    this.img.style.height = "100%"

    // Manejar carga de imagen
    this.img.onload = () => {
      this.naturalDimensions = {
        width: this.img.naturalWidth,
        height: this.img.naturalHeight
      }


      this.originalRatio = this.img.naturalWidth / this.img.naturalHeight

      // Aplicar dimensiones inteligentes
      this.applySmartDimensions()
    }

    this.img.onerror = () => {

      // Placeholder para imagen fallida
      this.dom.style.width = "300px";
      this.dom.style.height = "200px";
      this.dom.style.backgroundColor = "#f0f0f0";
      this.dom.style.border = "1px dashed #ccc";
      this.dom.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 14px;">Image failed to load</div>`;
    }

    // Si ya tenemos la imagen cargada, obtener dimensiones inmediatamente
    if (this.img.complete && this.img.naturalWidth > 0) {
      this.naturalDimensions = {
        width: this.img.naturalWidth,
        height: this.img.naturalHeight
      }
      this.originalRatio = this.img.naturalWidth / this.img.naturalHeight
      this.applySmartDimensions()
    }

    // Handle
    this.resizeHandle = document.createElement("div")
    this.resizeHandle.className = "resize-handle-single"

    this.renderUploadBadge(attrs);

    // Ensamblar
    this.dom.appendChild(this.img)
    this.dom.appendChild(this.resizeHandle)

    this.addResizeEvents()
    this.addClickSelection()

    // Verificar si está seleccionado inicialmente
    this.checkSelection()

    // Observar cambios en la selección
    this.view.dom.addEventListener("selectionchange", () => {
      this.checkSelection()
    })

    // Listener para cambios de ventana (responsividad)
    window.addEventListener('resize', this.handleWindowResize)

    // Aplicar bloqueo de descarga si está habilitado y NO es el owner
    if (this.blockFilesDownload && !this.isOwner) {
      this.applyDownloadBlock();
    }

    // Actualizar estado del bloqueo
    this.updateBlockStatus();

    // Debug log para verificar la configuración
  }

  private handleWindowResize = () => {
    // Reaplica dimensiones inteligentes cuando cambia el tamaño de ventana
    if (this.naturalDimensions.width > 0) {
      this.applySmartDimensions()
    }
  }

  private applyDownloadBlock() {
    // Bloquear botón derecho
    this.dom.addEventListener('contextmenu', (e) => {
      // Solo bloquear si NO es el owner
      if (this.blockFilesDownload && !this.isOwner) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear arrastrar imagen
    this.img.addEventListener('dragstart', (e) => {
      // Solo bloquear si NO es el owner
      if (this.blockFilesDownload && !this.isOwner) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear selección de texto
    this.dom.addEventListener('selectstart', (e) => {
      // Solo bloquear si NO es el owner
      if (this.blockFilesDownload && !this.isOwner) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear copiar
    this.dom.addEventListener('copy', (e) => {
      // Solo bloquear si NO es el owner
      if (this.blockFilesDownload && !this.isOwner) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear cortar
    this.dom.addEventListener('cut', (e) => {
      // Solo bloquear si NO es el owner
      if (this.blockFilesDownload && !this.isOwner) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Agregar clase CSS para estilos adicionales solo si NO es owner
    if (!this.isOwner) {
      this.dom.classList.add('download-blocked');
      this.img.classList.add('download-blocked');
    } else {
      // Si es owner, asegurar que NO tenga las clases de bloqueo
      this.dom.classList.remove('download-blocked');
      this.img.classList.remove('download-blocked');
    }
  }

  // Método para verificar el estado actual del bloqueo
  private getBlockStatus() {
    return {
      blockFilesDownload: this.blockFilesDownload,
      isOwner: this.isOwner,
      finalBlockDownload: this.blockFilesDownload && !this.isOwner,
      hasBlockClasses: this.dom.classList.contains('download-blocked'),
      imgHasBlockClasses: this.img.classList.contains('download-blocked')
    };
  }

  // Método para actualizar dinámicamente el estado del bloqueo
  private updateBlockStatus() {
    if (this.blockFilesDownload && !this.isOwner) {
      // Aplicar bloqueo
      this.dom.classList.add('download-blocked');
      this.img.classList.add('download-blocked');
    } else {
      // Remover bloqueo
      this.dom.classList.remove('download-blocked');
      this.img.classList.remove('download-blocked');
    }
  }

  private applySmartDimensions() {
    if (this.naturalDimensions.width === 0 || this.naturalDimensions.height === 0) {
      return
    }

    const smartDimensions = calculateSmartDimensions(
      this.naturalDimensions.width,
      this.naturalDimensions.height,
      this.node.attrs.width
    )

    // Aplicar dimensiones al wrapper, no a la imagen
    this.dom.style.width = smartDimensions.width
    this.dom.style.height = smartDimensions.height

    // La imagen siempre será 100% del wrapper
    this.img.style.width = "100%"
    this.img.style.height = "100%"
  }

  private checkSelection() {
    const { state } = this.view
    const { selection } = state
    const pos = this.getPos()

    const isSelected =
      selection instanceof NodeSelection && selection.from === pos && selection.to === pos + this.node.nodeSize

    if (isSelected !== this.selected) {
      this.selected = isSelected
      this.dom.setAttribute("data-selected", String(isSelected))
    }
  }

  private syncWrapperSize() {
    const wrapperWidth = this.dom.offsetWidth
    const wrapperHeight = this.dom.offsetHeight

    if (wrapperWidth > 0 && wrapperHeight > 0) {
      // Clasificar por tamaño para estilos específicos
      this.dom.classList.remove("small", "medium", "large")
      if (wrapperWidth < 250) this.dom.classList.add("small")
      else if (wrapperWidth < 450) this.dom.classList.add("medium")
      else this.dom.classList.add("large")
    } else {
    }
  }

  private addClickSelection() {
    this.dom.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()

      // Default behavior: select the entire node
      const tr = this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, this.getPos()))
      this.view.dispatch(tr)
      this.view.focus()
    })
  }

  private renderUploadBadge(attrs: any) {
    const existingBadge = this.dom.querySelector(".image-progress-badge");
    if (existingBadge) {
      existingBadge.remove();
    }

    const uploading =
      attrs["data-uploading"] === true ||
      attrs["data-uploading"] === "true" ||
      attrs["data-uploading"] === "1";
    const progress = attrs["data-progress"] ?? 0;

    if (!uploading) return;

    const badge = document.createElement("div");
    badge.className = "image-progress-badge";
    badge.style.position = "absolute";
    badge.style.top = "8px";
    badge.style.right = "8px";
    badge.style.background = "rgba(0, 0, 0, 0.6)";
    badge.style.color = "#fff";
    badge.style.padding = "4px 8px";
    badge.style.borderRadius = "4px";
    badge.style.fontSize = "12px";
    badge.style.zIndex = "10";
    badge.style.display = "flex";
    badge.style.alignItems = "center";
    badge.style.justifyContent = "center";
    badge.style.minWidth = "28px";
    badge.style.height = "28px";

    badge.textContent = `Subiendo: ${progress}%`;

    this.dom.appendChild(badge);
  }

  private addResizeEvents() {
    let startX = 0
    let startWidth = 0
    const config = isMobile() ? IMAGE_CONFIG.mobile : IMAGE_CONFIG.desktop

    const doResize = (e: MouseEvent) => {
      const dx = e.clientX - startX
      let newWidth = Math.max(config.minWidth, startWidth + dx)

      // Allow resizing up to the natural image size or config max, whichever is larger
      const maxAllowedWidth = Math.max(config.maxWidth, this.naturalDimensions.width || config.maxWidth)
      newWidth = Math.min(maxAllowedWidth, newWidth)

      // Limitar al ancho del contenedor, pero ser más flexible
      const containerWidth = this.dom.parentElement?.offsetWidth || window.innerWidth - 40
      newWidth = Math.min(newWidth, containerWidth)

      // Aplicar dimensiones al wrapper
      this.dom.style.width = `${newWidth}px`
      this.dom.style.height = "auto"

      // La imagen siempre será 100% del wrapper
      this.img.style.width = "100%"
      this.img.style.height = "100%"

      this.syncWrapperSize()
    }

    const stopResize = () => {
      document.removeEventListener("mousemove", doResize)
      document.removeEventListener("mouseup", stopResize)
      this.dom.classList.remove("resizing")

      // Actualizar el nodo con las nuevas dimensiones - usar solo width
      try {
        const pos = this.getPos()
        const newWidth = this.dom.style.width

        const tr = this.view.state.tr.setNodeMarkup(pos, undefined, {
          ...this.node.attrs,
          width: newWidth,
          // No actualizar height, dejarlo como "auto"
        })

        this.view.dispatch(tr)
      } catch (error) {
      }
    }

    this.resizeHandle.addEventListener("mousedown", (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      startX = e.clientX
      startWidth = this.img.offsetWidth
      this.dom.classList.add("resizing")

      document.addEventListener("mousemove", doResize)
      document.addEventListener("mouseup", stopResize)
    })
  }

  update(node: ProsemirrorNode) {
    if (node.type !== this.node.type) return false

    try {
      this.node = node
      const attrs = node.attrs

      // Determinar la fuente final de la imagen
      let finalSrc = attrs.src; // Default to original src

      // Solo usar signed_url si tenemos un media_file_id válido
      if (this.node.attrs.media_file_id && this.mediaFilesMap?.[this.node.attrs.media_file_id]) {
        const signedUrl = this.mediaFilesMap[this.node.attrs.media_file_id].signed_url;
        if (signedUrl) {
          finalSrc = signedUrl;

        }
      }

      // Solo cambiar src si es diferente
      if (this.img.src !== finalSrc) {
        this.img.src = finalSrc
      }

      // Aplicar width del nodo si existe y es válido
      if (attrs.width && attrs.width !== 'auto' && attrs.width !== '300px') {
        // Si hay un width específico (no el default), respetarlo
        this.dom.style.width = attrs.width
        this.dom.style.height = 'auto'
        this.img.style.width = '100%'
        this.img.style.height = '100%'
        this.syncWrapperSize()
      } else if (!this.naturalDimensions.width || this.naturalDimensions.width === 0) {
        // Solo aplicar dimensiones inteligentes si no tenemos las dimensiones naturales
        // o si es la primera carga
        if (this.img.complete && this.img.naturalWidth > 0) {
          this.naturalDimensions = {
            width: this.img.naturalWidth,
            height: this.img.naturalHeight
          }
          this.originalRatio = this.img.naturalWidth / this.img.naturalHeight
          this.applySmartDimensions()
        }
      }
      // Si ya tenemos dimensiones naturales y no hay width específico, 
      // mantener el tamaño actual (no resetear con smart dimensions)

      this.checkSelection()
      this.renderUploadBadge(attrs);
      return true
    } catch (error) {
      return false;
    }
  }

  destroy() {
    window.removeEventListener('resize', this.handleWindowResize)
  }
}